// Touring-routes overlay attach functions. Mirrors the controller pattern
// used by avalancheOverlay: each `attach…` returns
// `{ setVisible, setData, destroy }`.
//
// MapLibre version is used by RegionalMap3D + ResortTerrain3D; Leaflet
// version is used by SkiAreasMap (which is built on react-leaflet).

import { TOURING_ATTRIBUTION_LINE } from './touringRoutesData';

const TOURING_COLOR = '#a855f7';   // purple-500
const TOURING_CASING = '#ffffff';
const SOURCE_ID = 'pm-touring-routes';
const LAYER_LINE = 'touring-routes';
const LAYER_CASING = 'touring-routes-casing';

// ────────────────────────────────────────────────────────────────────────────
// MapLibre attach. `data` is a GeoJSON FeatureCollection whose features carry
// the unified schema produced by scripts/touring-routes-pipeline.
// ────────────────────────────────────────────────────────────────────────────
export function attachTouringRoutesMaplibre(map, data, { visible = true, beforeId } = {}) {
  if (!map) return null;
  let currentVisible = !!visible;

  const ensureLayers = () => {
    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: data || { type: 'FeatureCollection', features: [] },
        attribution: TOURING_ATTRIBUTION_LINE,
      });
    }
    if (!map.getLayer(LAYER_CASING)) {
      map.addLayer({
        id: LAYER_CASING,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': TOURING_CASING,
          'line-width': 4,
          'line-opacity': 0.55,
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          'visibility': currentVisible ? 'visible' : 'none',
        },
      }, beforeId);
    }
    if (!map.getLayer(LAYER_LINE)) {
      map.addLayer({
        id: LAYER_LINE,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': TOURING_COLOR,
          'line-width': 2.5,
          'line-dasharray': [3, 2],
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          'visibility': currentVisible ? 'visible' : 'none',
        },
      }, beforeId);
    }
  };

  const removeLayers = () => {
    for (const id of [LAYER_LINE, LAYER_CASING]) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
  };

  ensureLayers();

  return {
    setVisible(v) {
      currentVisible = !!v;
      ensureLayers();
      const vis = currentVisible ? 'visible' : 'none';
      if (map.getLayer(LAYER_LINE)) map.setLayoutProperty(LAYER_LINE, 'visibility', vis);
      if (map.getLayer(LAYER_CASING)) map.setLayoutProperty(LAYER_CASING, 'visibility', vis);
    },
    setData(next) {
      data = next;
      const src = map.getSource(SOURCE_ID);
      if (src) src.setData(next || { type: 'FeatureCollection', features: [] });
    },
    destroy() {
      removeLayers();
    },
    layerIds: { line: LAYER_LINE, casing: LAYER_CASING },
  };
}

// Build the popup HTML used by both 2D and 3D click handlers. Plain strings
// (no JSX) so it works in MapLibre's setHTML and Leaflet's setContent.
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

export function buildTouringPopupHTML(properties = {}) {
  const name = properties.name || 'Unnamed touring route';
  const meta = [];
  if (properties.difficulty) meta.push(esc(properties.difficulty));
  if (properties.country) meta.push(esc(properties.country));
  const stats = [];
  if (properties.ascentM) stats.push(`<span><b>${Math.round(properties.ascentM)} m</b> ascent</span>`);
  if (properties.lengthM) stats.push(`<span><b>${(properties.lengthM / 1000).toFixed(1)} km</b> length</span>`);
  const sourceLabel = properties.source === 'skitourenguru' ? 'Skitourenguru' : 'OpenStreetMap';

  return `
    <div class="touring-popup">
      <div class="touring-popup-title">${esc(name)}</div>
      ${meta.length ? `<div class="touring-popup-meta">${meta.join(' · ')}</div>` : ''}
      ${stats.length ? `<div class="touring-popup-stats">${stats.join('')}</div>` : ''}
      <div class="touring-popup-source">
        <a href="${esc(properties.sourceUrl || '#')}" target="_blank" rel="noopener noreferrer">${sourceLabel}</a>
        · <span class="touring-popup-license">${esc(properties.attribution || '')}</span>
      </div>
    </div>
  `;
}

// ────────────────────────────────────────────────────────────────────────────
// Leaflet attach. `leafletMap` is an L.Map; `L` is passed in to avoid
// importing leaflet in this module (SkiAreasMap already imports it once).
// ────────────────────────────────────────────────────────────────────────────
export function attachTouringRoutesLeaflet(leafletMap, L, data, {
  visible = true,
  minZoom = 10,
} = {}) {
  if (!leafletMap || !L) return null;
  let currentVisible = !!visible;
  let currentData = data || { type: 'FeatureCollection', features: [] };
  let layerGroup = null;

  const styleFn = () => ({
    color: TOURING_COLOR,
    weight: 2.5,
    opacity: 0.95,
    dashArray: '5,3',
    lineCap: 'round',
    lineJoin: 'round',
  });

  const onEachFeature = (feature, layer) => {
    layer.bindPopup(buildTouringPopupHTML(feature.properties || {}), {
      autoPanPadding: [40, 40],
      maxWidth: 280,
      className: 'touring-popup-wrapper',
    });
    layer.on('mouseover', () => layer.setStyle({ weight: 4 }));
    layer.on('mouseout', () => layer.setStyle({ weight: 2.5 }));
  };

  const buildLayer = () => {
    return L.geoJSON(currentData, { style: styleFn, onEachFeature });
  };

  const sync = () => {
    const zoom = leafletMap.getZoom();
    const shouldRender = currentVisible && zoom >= minZoom;
    if (shouldRender && !layerGroup) {
      layerGroup = buildLayer();
      leafletMap.addLayer(layerGroup);
    } else if (!shouldRender && layerGroup) {
      leafletMap.removeLayer(layerGroup);
      layerGroup = null;
    }
  };

  const onZoomEnd = () => sync();
  leafletMap.on('zoomend', onZoomEnd);
  sync();

  return {
    setVisible(v) {
      currentVisible = !!v;
      sync();
    },
    setData(next) {
      currentData = next || { type: 'FeatureCollection', features: [] };
      if (layerGroup) {
        leafletMap.removeLayer(layerGroup);
        layerGroup = null;
      }
      sync();
    },
    destroy() {
      leafletMap.off('zoomend', onZoomEnd);
      if (layerGroup) {
        leafletMap.removeLayer(layerGroup);
        layerGroup = null;
      }
    },
  };
}
